import React, { useEffect, useState } from 'react';
import { Button, Col, Divider, Form, Input, Row, Switch, Upload, Select, Image } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { getImageFallbackUrl } from '../../utils/helpers';

const currencies = ['IQD', 'USD', 'EUR', 'SAR', 'SYP'];

function CreateUpdateAddonForm({
  selectedAddonItem = {},
  languageList,
  onSubmit,
}) {
  const isUpdate = !!selectedAddonItem?.id;
  const [imageUrl, setImageUrl] = useState();
  const [mainImageError, setMainImageError] = useState('');
  const [mainImageFileList, setMainImageFileList] = useState([]);
  const [failedMainImages, setFailedMainImages] = useState(new Set());

  useEffect(() => {
    if (selectedAddonItem.image) {
      setImageUrl(selectedAddonItem.image);
    }
  }, [selectedAddonItem]);

  function getBase64(img, callback) {
    if (!(img instanceof Blob)) return;
    const reader = new FileReader();
    reader.addEventListener('load', () => callback(reader.result));
    reader.readAsDataURL(img);
  }

  const handleSubmit = async values => {
    const mainImageFile = mainImageFileList.length > 0 ? mainImageFileList[0] : null;
    const payload = {
      ...values,
      image: mainImageFile ? { file: { originFileObj: mainImageFile.originFileObj } } : undefined,
    };
    onSubmit(payload);
  };

  const handleImageChange = info => {
    const file = info.file;
    if (file && (file.originFileObj || file.uid)) {
      setMainImageFileList(info.fileList);
      if (file.originFileObj) {
        getBase64(file.originFileObj, result => setImageUrl(result));
      } else if (file.url || file.thumbUrl || file.preview) {
        setImageUrl(file.url || file.thumbUrl || file.preview);
      }
      setMainImageError('');
    } else if (info.fileList.length === 0) {
      setMainImageFileList([]);
      setImageUrl('');
      setMainImageError('');
    }
  };

  const handleMainImageError = url => {
    if (!failedMainImages.has(url)) {
      setFailedMainImages(prev => new Set([...prev, url]));
    }
  };

  const beforeUploadMainImage = file => {
    const MAX_SIZE_BYTES = 0.75 * 1024 * 1024;
    const hasImageMimeType = file.type && file.type.startsWith('image/');
    const hasImageExtension = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(file.name || '');
    const isImage = hasImageMimeType || hasImageExtension;
    if (!isImage) {
      setMainImageError('Only image files are allowed.');
      return Upload.LIST_IGNORE;
    }
    if (typeof file.size === 'number' && file.size > MAX_SIZE_BYTES) {
      setMainImageError('Image must be 0.75 MB or smaller.');
      return Upload.LIST_IGNORE;
    }
    setMainImageError('');
    return false;
  };

  const handleMainImageRemove = () => {
    setMainImageFileList([]);
    setImageUrl('');
    setMainImageError('');
    return true;
  };

  return (
    <Form
      onFinish={handleSubmit}
      name="createUpdateAddonForm"
      className="form-wrapper"
      layout="vertical"
      size="large"
      initialValues={{
        currency: 'IQD',
        isInStock: true,
        ...selectedAddonItem,
      }}
    >
      <Row gutter={16}>
        <Col span={12}>
          {languageList.map(lan => (
            <Form.Item
              key={lan.id}
              name={`name.${lan.id}`}
              label={`Name in ${lan.name}`}
              rules={[{ required: true, message: `Please enter name in ${lan.name}!` }]}
            >
              <Input placeholder={`Addon name in ${lan.name}`} />
            </Form.Item>
          ))}
        </Col>
      </Row>
      <Divider />
      <Row gutter={16}>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="price" label="Price" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="Price" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="priceWholesale" label="Price (Wholesale)">
            <Input placeholder="Optional" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="currency" label="Currency">
            <Select options={currencies.map(c => ({ value: c, label: c }))} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="isInStock" label="In stock" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
      </Row>
      <Divider />
      <Row>
        <Col span={12}>
          <Form.Item label="Addon image" name="image" className="product-image-upload">
            <Upload
              listType="picture-card"
              fileList={mainImageFileList}
              onChange={handleImageChange}
              beforeUpload={beforeUploadMainImage}
              onRemove={handleMainImageRemove}
              accept="image/*"
              maxCount={1}
              showUploadList
            >
              {imageUrl ? (
                <Image
                  src={failedMainImages.has(imageUrl) ? getImageFallbackUrl(imageUrl) : imageUrl}
                  alt="addon"
                  style={{ width: '100%' }}
                  preview={false}
                  onError={() => handleMainImageError(imageUrl)}
                />
              ) : (
                <>
                  <PlusOutlined />
                  <div className="ant-upload-text">Upload</div>
                </>
              )}
            </Upload>
            {mainImageError ? <div style={{ color: '#ff4d4f', marginTop: 8 }}>{mainImageError}</div> : null}
          </Form.Item>
        </Col>
      </Row>
      <Row className="m-t-30 f-r">
        <Form.Item>
          <Button type="primary" htmlType="submit" size="large" disabled={!!mainImageError}>
            {isUpdate ? 'Save' : 'Create'}
          </Button>
        </Form.Item>
      </Row>
    </Form>
  );
}

export default CreateUpdateAddonForm;
